import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

export interface PushNotificationData {
  type?: string;
  propertyId?: string;
  offerId?: string;
  [key: string]: any;
}

class PushNotificationService {
  private isInitialized = false;
  private currentToken: string | null = null;
  private tokenResolvers: Array<(token: string | null) => void> = [];
  private navigationCallback: ((data: PushNotificationData) => void) | null = null;

  /**
   * Check if push notifications are available on this platform
   */
  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get the current platform
   */
  getPlatform(): 'ios' | 'android' | 'web' {
    return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
  }

  /**
   * Set navigation callback for handling notification taps
   */
  setNavigationCallback(callback: (data: PushNotificationData) => void) {
    this.navigationCallback = callback;
  }

  /**
   * Initialize push notifications
   */
  async initialize(): Promise<boolean> {
    if (!this.isAvailable()) {
      logger.info('[PushNotifications] Not available on this platform');
      return false;
    }

    if (this.isInitialized) {
      logger.info('[PushNotifications] Already initialized');
      return true;
    }

    try {
      // Check current permission status
      const permissionStatus = await PushNotifications.checkPermissions();
      logger.info('[PushNotifications] Current permission status:', permissionStatus);

      if (permissionStatus.receive === 'prompt') {
        // Request permission
        const requestResult = await PushNotifications.requestPermissions();
        if (requestResult.receive !== 'granted') {
          logger.warn('[PushNotifications] Permission not granted');
          return false;
        }
      } else if (permissionStatus.receive !== 'granted') {
        logger.warn('[PushNotifications] Permission denied');
        return false;
      }

      // Set up listeners FIRST so the registration event is captured
      this.setupListeners();

      // Register with FCM
      await PushNotifications.register();

      this.isInitialized = true;
      logger.info('[PushNotifications] Successfully initialized');
      return true;
    } catch (error) {
      logger.error('[PushNotifications] Initialization error:', error);
      return false;
    }
  }

  /**
   * Set up push notification event listeners
   */
  private setupListeners() {
    // Registration success - cache token and resolve any waiting callers
    PushNotifications.addListener('registration', (token: Token) => {
      logger.info('[PushNotifications] Registration successful, token:', token.value.substring(0, 20) + '...');
      this.currentToken = token.value;
      this.tokenResolvers.forEach(resolve => resolve(token.value));
      this.tokenResolvers = [];
    });

    // Registration error
    PushNotifications.addListener('registrationError', (error: any) => {
      logger.error('[PushNotifications] Registration error:', error);
    });

    // Foreground notification received
    PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      logger.info('[PushNotifications] Notification received in foreground:', notification);
      
      // Show toast for foreground notifications
      toast(notification.title || 'New Notification', {
        description: notification.body,
        action: notification.data?.propertyId ? {
          label: 'View',
          onClick: () => {
            if (this.navigationCallback && notification.data) {
              this.navigationCallback(notification.data as PushNotificationData);
            }
          }
        } : undefined
      });
    });

    // Notification tapped
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      logger.info('[PushNotifications] Notification action performed:', action);
      
      if (this.navigationCallback && action.notification.data) {
        this.navigationCallback(action.notification.data as PushNotificationData);
      }
    });
  }

  /**
   * Get the current FCM token
   */
  async getToken(): Promise<string | null> {
    if (!this.isAvailable()) {
      return null;
    }

    // Return cached token immediately if available
    if (this.currentToken) {
      return this.currentToken;
    }

    // Check permissions before waiting
    try {
      const permissionStatus = await PushNotifications.checkPermissions();
      if (permissionStatus.receive !== 'granted') {
        return null;
      }
    } catch {
      return null;
    }

    // Wait up to 8s for the registration event to fire
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.tokenResolvers = this.tokenResolvers.filter(r => r !== resolve);
        logger.warn('[PushNotifications] Token wait timed out');
        resolve(null);
      }, 8000);

      this.tokenResolvers.push((token) => {
        clearTimeout(timeout);
        resolve(token);
      });
    });
  }

  /**
   * Register push token with the backend
   */
  async registerToken(userId: string, userType: 'buyer' | 'seller'): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    try {
      const token = await this.getToken();
      if (!token) {
        logger.warn('[PushNotifications] No token available');
        return false;
      }

      const platform = this.getPlatform();
      logger.info(`[PushNotifications] Registering token for ${userType} on ${platform}`);

      if (userType === 'buyer') {
        // Get buyer profile id from user_id
        const { data: buyerProfile } = await supabase
          .from('buyer_profiles')
          .select('id')
          .eq('user_id', userId)
          .single();

        if (buyerProfile) {
          const { error } = await supabase
            .from('buyer_profiles')
            .update({
              push_token: token,
              push_notifications_enabled: true,
              device_platform: platform
            })
            .eq('id', buyerProfile.id);

          if (error) {
            logger.error('[PushNotifications] Error saving buyer token:', error);
            return false;
          }
        }
      } else {
        // Seller - update profiles table
        const { error } = await supabase
          .from('profiles')
          .update({
            push_token: token,
            push_notifications_enabled: true,
            device_platform: platform
          })
          .eq('id', userId);

        if (error) {
          logger.error('[PushNotifications] Error saving seller token:', error);
          return false;
        }
      }

      logger.info('[PushNotifications] Token registered successfully');
      return true;
    } catch (error) {
      logger.error('[PushNotifications] Error registering token:', error);
      return false;
    }
  }

  /**
   * Clear push token on logout
   */
  async clearToken(userId: string, userType: 'buyer' | 'seller'): Promise<void> {
    if (!this.isAvailable()) {
      return;
    }

    try {
      if (userType === 'buyer') {
        const { data: buyerProfile } = await supabase
          .from('buyer_profiles')
          .select('id')
          .eq('user_id', userId)
          .single();

        if (buyerProfile) {
          await supabase
            .from('buyer_profiles')
            .update({
              push_token: null,
              device_platform: null
            })
            .eq('id', buyerProfile.id);
        }
      } else {
        await supabase
          .from('profiles')
          .update({
            push_token: null,
            device_platform: null
          })
          .eq('id', userId);
      }

      logger.info('[PushNotifications] Token cleared');
    } catch (error) {
      logger.error('[PushNotifications] Error clearing token:', error);
    }
  }

  /**
   * Update push notification enabled status
   */
  async updatePushEnabled(userId: string, userType: 'buyer' | 'seller', enabled: boolean): Promise<boolean> {
    try {
      if (userType === 'buyer') {
        const { data: buyerProfile } = await supabase
          .from('buyer_profiles')
          .select('id')
          .eq('user_id', userId)
          .single();

        if (buyerProfile) {
          const { error } = await supabase
            .from('buyer_profiles')
            .update({ push_notifications_enabled: enabled })
            .eq('id', buyerProfile.id);

          if (error) {
            logger.error('[PushNotifications] Error updating buyer preference:', error);
            return false;
          }
        }
      } else {
        const { error } = await supabase
          .from('profiles')
          .update({ push_notifications_enabled: enabled })
          .eq('id', userId);

        if (error) {
          logger.error('[PushNotifications] Error updating seller preference:', error);
          return false;
        }
      }

      return true;
    } catch (error) {
      logger.error('[PushNotifications] Error updating push enabled:', error);
      return false;
    }
  }

  /**
   * Check if push notifications are enabled for user
   */
  async isPushEnabled(userId: string, userType: 'buyer' | 'seller'): Promise<boolean> {
    try {
      if (userType === 'buyer') {
        const { data: buyerProfile } = await supabase
          .from('buyer_profiles')
          .select('push_notifications_enabled')
          .eq('user_id', userId)
          .single();

        return buyerProfile?.push_notifications_enabled ?? true;
      } else {
        const { data: profile } = await supabase
          .from('profiles')
          .select('push_notifications_enabled')
          .eq('id', userId)
          .single();

        return profile?.push_notifications_enabled ?? true;
      }
    } catch (error) {
      logger.error('[PushNotifications] Error checking push enabled:', error);
      return true;
    }
  }
}

export const pushNotificationService = new PushNotificationService();
